import { useEffect, useState } from 'react';
import AdminTabs from '@/features/admin/components/AdminTabs';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { usePaginatedList } from '@/shared/hooks/usePaginatedList';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { listFeedback } from '@/features/admin/services/adminService';
import type { AdminFeedbackItem } from '@/shared/types';

const PAGE_SIZE = 200;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

// Collapsed to 2 lines by default so a long submission doesn't blow out the row —
// "Show more"/"Show less" toggles the clamp per-row.
function FeedbackMessageCell({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="feedback-message-cell">
      <div className={expanded ? undefined : 'feedback-message-cell__text--collapsed'}>{message}</div>
      {message.length > 140 && (
        <button
          type="button"
          className="feedback-message-cell__toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

const feedbackColumnHelper = createDataTableColumns<AdminFeedbackItem>();

const feedbackColumns = feedbackColumnHelper.columns([
  feedbackColumnHelper.accessor((item) => formatDateTime(item.createdAt), {
    id: 'createdAt',
    header: 'Date',
    filterFn: 'includesString',
  }),
  feedbackColumnHelper.accessor('name', {
    header: 'Name',
    enableSorting: false,
    filterFn: 'includesString',
    cell: (info) => info.getValue() || '—',
  }),
  feedbackColumnHelper.accessor('email', {
    header: 'Email',
    filterFn: 'includesString',
  }),
  feedbackColumnHelper.accessor('message', {
    header: 'Feedback',
    enableSorting: false,
    filterFn: 'includesString',
    meta: { align: 'left' },
    cell: (info) => <FeedbackMessageCell message={info.getValue()} />,
  }),
]);

export default function AdminFeedbackPage() {
  const { items, loading, loadingMore, error, setError, hasMore, load, loadMore } =
    usePaginatedList<AdminFeedbackItem>((nextOffset) =>
      listFeedback({
        limit: PAGE_SIZE,
        offset: nextOffset,
      }),
    );

  useEffect(() => {
    load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-header">
        <h1>Feedback</h1>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : error && items.length === 0 ? (
        <ErrorBanner message={error} />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No feedback yet</div>
        </div>
      ) : (
        <>
          <DataTable
            columns={feedbackColumns}
            data={items}
            getRowId={(item) => item.id}
            enablePagination={true}
            pageSize={20}
          />

          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
          {hasMore && (
            <button
              type="button"
              className="button secondary wallet-activity__more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more from server'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
